// TP-Last-Minute API client.
//
// Isolated from the shared services/api.js (small PR surface, no risk to the
// shared axios instance) — mirrors services/scoutRequestService.js. Paths + shapes
// are the M1 contract (dashboard/last_minute_views.py + urls.py), verified against
// the backend worktree, NEVER guessed:
//   feed (browse)     → /dashboard/last-minute/feed/
//   listings/composer → /dashboard/last-minute/listings/
//   message-requests  → /dashboard/last-minute/message-requests/
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_ENDPOINT;

const authApi = axios.create({ baseURL: API_BASE_URL, withCredentials: true });

// Same Bearer-token attachment the club-network / scout-request clients use.
authApi.interceptors.request.use((config) => {
  try {
    const tokens = JSON.parse(localStorage.getItem("tokens") || "null");
    if (tokens?.access) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${tokens.access}`;
    }
  } catch (e) {
    /* no token — request proceeds and the server returns 401 */
  }
  return config;
});

const fail = (error) => ({
  success: false,
  status: error?.response?.status,
  data: error?.response?.data,
  error:
    error?.response?.data?.detail ||
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    (typeof error?.response?.data === "object"
      ? Object.values(error.response.data)[0]
      : null) ||
    "Something went wrong. Please try again.",
});

const isCancel = (error) => error?.name === "CanceledError" || error?.name === "AbortError";

export const lastMinuteService = {
  // ── Browse feed (agent sees clubs_agents listings; club sees both minus own) ──
  getFeed: async (params, signal) => {
    try {
      const res = await authApi.get("/dashboard/last-minute/feed/", { params, signal });
      return { success: true, data: res.data.results || [], count: res.data.count ?? 0 };
    } catch (error) {
      if (isCancel(error)) throw error;
      return fail(error);
    }
  },

  getListing: async (id, signal) => {
    try {
      const res = await authApi.get(`/dashboard/last-minute/feed/${id}/`, { signal });
      return { success: true, data: res.data };
    } catch (error) {
      if (isCancel(error)) throw error;
      return fail(error);
    }
  },

  // Club → listing agent: opens the standard club↔agent chat. { channel_id }.
  contactAgent: async (id) => {
    try {
      const res = await authApi.post(`/dashboard/last-minute/feed/${id}/contact/`);
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  addToRadar: async (id) => {
    try {
      const res = await authApi.post(`/dashboard/last-minute/feed/${id}/add-to-radar/`);
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  // ── My Listings + composer (agent) ──
  // state = active|expired|unpublished (omit for all); scope=agency for AgencyAdmin.
  getMyListings: async (params, signal) => {
    try {
      const res = await authApi.get("/dashboard/last-minute/listings/", { params, signal });
      return { success: true, data: res.data.results || [], count: res.data.count ?? 0 };
    } catch (error) {
      if (isCancel(error)) throw error;
      return fail(error);
    }
  },

  // The composer picker source (Invariant 1: own roster only).
  getRoster: async (signal) => {
    try {
      const res = await authApi.get("/dashboard/last-minute/listings/roster/", { signal });
      return { success: true, data: res.data };
    } catch (error) {
      if (isCancel(error)) throw error;
      return fail(error);
    }
  },

  createListing: async (payload) => {
    try {
      const res = await authApi.post("/dashboard/last-minute/listings/", payload);
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  updateListing: async (id, payload) => {
    try {
      const res = await authApi.patch(`/dashboard/last-minute/listings/${id}/`, payload);
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  extend: async (id) => {
    try {
      const res = await authApi.post(`/dashboard/last-minute/listings/${id}/extend/`);
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  renew: async (id) => {
    try {
      const res = await authApi.post(`/dashboard/last-minute/listings/${id}/renew/`);
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  unpublish: async (id) => {
    try {
      const res = await authApi.post(`/dashboard/last-minute/listings/${id}/unpublish/`);
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  // Owner grants an identity reveal to ONE club (Invariant 3). Idempotent.
  reveal: async (id, clubId) => {
    try {
      const res = await authApi.post(`/dashboard/last-minute/listings/${id}/reveal/`, { club_id: clubId });
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  // ── Agent → agent opportunity requests (message-request backbone) ──
  sendOpportunity: async ({ listingId, recipientAgentId, message }) => {
    try {
      const res = await authApi.post("/dashboard/last-minute/message-requests/", {
        listing_id: listingId,
        recipient_agent_id: recipientAgentId,
        message: message || "",
      });
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  getReceivedRequests: async (signal) => {
    try {
      const res = await authApi.get("/dashboard/last-minute/message-requests/received/", { signal });
      return { success: true, data: res.data.results || [] };
    } catch (error) {
      if (isCancel(error)) throw error;
      return fail(error);
    }
  },

  getSentRequests: async (signal) => {
    try {
      const res = await authApi.get("/dashboard/last-minute/message-requests/sent/", { signal });
      return { success: true, data: res.data.results || [] };
    } catch (error) {
      if (isCancel(error)) throw error;
      return fail(error);
    }
  },

  acceptRequest: async (id) => {
    try {
      const res = await authApi.post(`/dashboard/last-minute/message-requests/${id}/accept/`);
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  ignoreRequest: async (id) => {
    try {
      const res = await authApi.post(`/dashboard/last-minute/message-requests/${id}/ignore/`);
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },
};

export default lastMinuteService;
